/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Heading, HFlow, Tabs, VFlow } from 'bold-ui'
import useSession from 'components/auth/useSession'
import { PageContainer } from 'components/layout/PageContainer'
import { BadgeNotificacao } from 'components/notificacao/BadgeNotificacao'
import { TabLink } from 'components/route'
import { useFlags } from 'config/useFlagsContext'
import { useCuidadoCompartilhadoNotificacaoQuery } from 'graphql/hooks.generated'
import { TipoTopicoNotificacaoEnum } from 'graphql/types.generated'
import { useTopicoNotificacaoFiltered } from 'hooks/notificacao/useTopicoNotificacaoFiltered'
import { Fragment, useMemo } from 'react'
import { useRouteMatch } from 'react-router'
import { isUndefinedOrNull } from 'util/checks'

import { grupoTiposUnidadeSaudeEspecializadoCC } from '../acesso-cuidadoCompartilhado'
import { LCC_TABS_ROUTE } from '../CuidadosCompartilhadosRootView'

interface ListaCuidadoCompartilhadoTabsProps {
  isGerente?: boolean
}

export const ListaCuidadoCompartilhadoHeader = (props: Readonly<ListaCuidadoCompartilhadoTabsProps>) => {
  const { isGerente = false } = props
  const { hasTipoUnidadeSaudeAuth } = useSession()
  const { PILOTO_VINCULACAO_CUIDADO_ENABLED } = useFlags()
  const match = useRouteMatch()
  const url = `${match.path.substring(0, match.path.lastIndexOf('/'))}`

  const cuidadoCompartilhadoComNotificacaoIds: ID[] = useTopicoNotificacaoFiltered(
    TipoTopicoNotificacaoEnum.CUIDADO_COMPARTILHADO,
    { filtrarPor: 'lido' }
  ).map((topico) => topico.codigoTopicoExterno)
  const hasNotificacao = cuidadoCompartilhadoComNotificacaoIds.length > 0

  const {
    data: { acesso },
  } = useSession()
  const acessoId = acesso?.id
  const {
    data: { cuidadosCompartilhado: { content: cuidadoCompartilhadoNotificacao = [] } = {} } = {},
  } = useCuidadoCompartilhadoNotificacaoQuery({
    fetchPolicy: 'cache-first',
    variables: {
      input: {
        cuidadosCompartilhadoIds: cuidadoCompartilhadoComNotificacaoIds,
        somenteUnidadeAcesso: false,
        somenteUnidadeExecutante: false,
        somenteSemExecutante: false,
        solicitadosPelaMinhaEquipe: false,
        executadosPelaMinhaEquipe: false,
        somenteUnidadesBasicas: false,
        somenteNaoLidos: false,
      },
    },
    skip: !(hasNotificacao && Boolean(acessoId)),
    context: { newRequest: true },
  })

  const {
    solicitante: notificacaoSolicitanteCount,
    executante: notificacaoExecutanteCount,
    unidade: notificacaoUnidadeCount,
  } = useMemo(() => categorizarContagemNotificacoes(cuidadoCompartilhadoNotificacao, acessoId), [
    cuidadoCompartilhadoNotificacao,
    acessoId,
  ])

  return (
    <PageContainer fluid style={styles.pageContainer}>
      <VFlow>
        <Heading level={1} style={styles.heading(isGerente)}>
          Cuidados compartilhados
        </Heading>
        {!isGerente && (
          <Tabs>
            {PILOTO_VINCULACAO_CUIDADO_ENABLED && hasTipoUnidadeSaudeAuth(grupoTiposUnidadeSaudeEspecializadoCC) ? (
              <Fragment>
                <TabLink to={`${url}/${LCC_TABS_ROUTE['EXECUTANTE']}`}>
                  <HFlow alignItems='center' hSpacing={0.5}>
                    Compartilhados comigo
                    <BadgeNotificacao type='large' value={notificacaoExecutanteCount} color='blue' />
                  </HFlow>
                </TabLink>
                <TabLink to={`${url}/${LCC_TABS_ROUTE['UNIDADE']}`}>
                  <HFlow alignItems='center' hSpacing={0.5}>
                    Compartilhados com a unidade
                    <BadgeNotificacao type='large' value={notificacaoUnidadeCount} color='blue' />
                  </HFlow>
                </TabLink>
              </Fragment>
            ) : (
              <Fragment>
                <TabLink to={`${url}/${LCC_TABS_ROUTE['SOLICITANTE']}`}>
                  <HFlow alignItems='center' hSpacing={0.5}>
                    Solicitados por mim
                    <BadgeNotificacao type='large' value={notificacaoSolicitanteCount} color='blue' />
                  </HFlow>
                </TabLink>

                <TabLink to={`${url}/${LCC_TABS_ROUTE['EXECUTANTE']}`}>
                  <HFlow alignItems='center' hSpacing={0.5}>
                    Compartilhados comigo
                    <BadgeNotificacao type='large' value={notificacaoExecutanteCount} color='blue' />
                  </HFlow>
                </TabLink>
              </Fragment>
            )}
          </Tabs>
        )}
      </VFlow>
    </PageContainer>
  )
}

const styles = {
  heading: (isGerente) => css`
    margin-top: 0.5rem;
    margin-bottom: ${isGerente ? '1rem' : '0'};
  `,
  pageContainer: css`
    padding: 1rem 2rem 0rem 2rem;
  `,
}

type Notificacao = {
  id: string
  lotacaoExecutanteAtual?: {
    id: string
  } | null
  lotacaoSolicitante: {
    id: string
  }
}

type ContagemNotificacoes = {
  solicitante: number
  executante: number
  unidade: number
}

const categorizarContagemNotificacoes = (
  notificacoes: Notificacao[],
  acessoId: ID | null | undefined
): ContagemNotificacoes => {
  const contagemInicial: ContagemNotificacoes = {
    solicitante: 0,
    executante: 0,
    unidade: 0,
  }

  if (!Array.isArray(notificacoes) || isUndefinedOrNull(acessoId)) {
    return contagemInicial
  }

  return notificacoes.reduce((contagens, cuidado) => {
    if (cuidado.lotacaoSolicitante.id === acessoId) {
      return { ...contagens, solicitante: contagens.solicitante + 1 }
    }

    if (cuidado.lotacaoExecutanteAtual?.id === acessoId) {
      return { ...contagens, executante: contagens.executante + 1 }
    }

    return { ...contagens, unidade: contagens.unidade + 1 }
  }, contagemInicial)
}
